/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends' settings drawer (docs/CONTRACT-fleet-governance.md sections 8.3, 10 and 12.3): how long the governance copy is
 * kept (1 to 365 days; PUT /v1/admin/governance/settings, audited `admin.governance_settings_changed`), and read-only lines
 * for the copy's rows, its oldest day and the last catch-up. Auditors see it read-only.
 */
import { useEffect, useState } from 'react';
import { RETENTION_HELP, backfillText, trendsApi, trendsFailure, validateRetention } from '../../lib/trends';
import { Callout, Drawer, Field, NumberInput, SaveBar, Skeleton } from '../../ui';
import { count, dateTime } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { Facts, LoadError } from '../fleet/parts';

/** Mounted only while open, so the settings are read when the drawer opens. */
export function TrendsSettingsDrawer({ open, canEdit, onClose }: { open: boolean; canEdit: boolean; onClose: () => void }) {
  return open ? <SettingsDrawer canEdit={canEdit} onClose={onClose} /> : null;
}

function SettingsDrawer({ canEdit, onClose }: { canEdit: boolean; onClose: () => void }) {
  const { load, reload, reloading, setData } = useLoad(() => trendsApi.settings(), 'trends-settings');
  const view = load.state === 'ready' ? load.data : null;
  const [days, setDays] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    if (view) setDays(String(view.settings.rowsRetentionDays));
  }, [view]);

  const stored = view ? String(view.settings.rowsRetentionDays) : '';
  const changes = view && days.trim() !== stored ? 1 : 0;

  const save = async () => {
    if (!view) return;
    const problem = validateRetention(days);
    if (problem) {
      setSaveError(problem);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const next = await trendsApi.saveSettings({ rowsRetentionDays: Number(days.trim()) });
      setData(() => next);
      setSaved(`The governance copy is now kept for ${count(next.settings.rowsRetentionDays)} days. The change is in the audit trail.`);
    } catch (error) {
      setSaveError(trendsFailure(error, 'The setting could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Trends settings"
      description="How long the governance copy keeps the audit rows Trends counts"
      footer={canEdit && view ? (
        <SaveBar
          changes={changes}
          saving={saving}
          error={saveError}
          saved={saved}
          onSave={() => void save()}
          onDiscard={() => { setDays(stored); setSaveError(null); }}
        />
      ) : undefined}
    >
      {load.state === 'loading' ? <div className="ui-form" aria-busy="true"><Skeleton size="lg" /><Skeleton /></div> : null}
      {load.state === 'failed' ? <LoadError title="The Trends settings could not be read" error={load.error} words={(error) => trendsFailure(error)} onRetry={reload} retrying={reloading} /> : null}
      {view ? (
        <div className="ui-form">
          {!canEdit ? <Callout tone="info">Read-only: auditors see the settings; only IT admins change them.</Callout> : null}
          <Field label="Keep the governance copy for (days)" help={RETENTION_HELP}>
            <NumberInput aria-label="Keep the governance copy for (days)" value={days} disabled={!canEdit} onChange={(event) => { setSaved(null); setDays(event.target.value); }} />
          </Field>
          <Facts label="The governance copy" rows={[
            ['Rows', count(view.copy.rows)],
            ['Oldest day', view.copy.oldestDay ?? 'None yet'],
            ['Newest day', view.copy.newestDay ?? 'None yet'],
            ['Last catch-up', backfillText(view.backfill, (at) => dateTime(at))],
          ]} />
        </div>
      ) : null}
    </Drawer>
  );
}
